import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import { insertOrUpdateAgent } from "../../features/agent/agentSlice";
import { fetchSettingDetailsByUserId } from "../../features/settings/settingsSlice";
import {
  agentValidationSchema,
  getAgentInitialValues,
} from "../../constants/agent.constants";
import { Icons } from "../../constants/icon-library";
import { getIcon } from "../../constants/constants";

const CreateAgent = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { user } = useSelector((state) => state.user);
  const { userSettings } = useSelector((state) => state.setting);

  useEffect(() => {
    if (user?.uuid) {
      dispatch(fetchSettingDetailsByUserId());
    }
  }, [user?.uuid, dispatch]);

  const assistantOptions = useMemo(() => {
    if (!userSettings?.length) return [];
    return userSettings.map((setting) => ({
      label: setting.assistantName || setting.assistantId?.name,
      value: setting.uuid,
      name: setting.assistantId?.name
    }));
  }, [userSettings]);

  const formik = useFormik({
    initialValues: getAgentInitialValues(false, null, user),
    validationSchema: agentValidationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const response = await dispatch(
          insertOrUpdateAgent({
            name: values.name,
            phoneNumber: values.phoneNumber,
            settingIds: values.assistants,
            accountId: user?.org,
            userId: user?.uuid,
          }),
        );

        const createdAgent = response?.payload?.agent;
        if (createdAgent?.uuid) {
          toast.success("Agent created successfully.");
          navigate(`/agent/${createdAgent.uuid}`);
        }
      } catch (err) {
        console.error("Failed to create agent:", err);
        toast.error("Failed to create agent.");
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <div className="content mt-4">
      <div className="row">
        <div className="card border p-0">
          <div className="card-header p-4 border-bottom bg-body d-flex justify-content-between align-items-center">
            <h3 className="mb-0 d-flex align-items-center">Create New Agent</h3>
          </div>

          <div className="card-body">
            <div className="col-12">
              <form onSubmit={formik.handleSubmit}>
                <div className="mb-4">
                  <label className="fw-bold mb-2">
                    Agent Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    className={`form-control ${
                      formik.touched.name && formik.errors.name
                        ? "is-invalid"
                        : ""
                    }`}
                    placeholder="Enter agent name"
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.name && formik.errors.name && (
                    <div className="invalid-feedback">{formik.errors.name}</div>
                  )}
                </div>

                {/* <div className="mb-4">
                  <label className="fw-bold mb-2">Phone Number</label>
                  <select
                    name="phoneNumber"
                    className="form-select"
                    value={formik.values.phoneNumber}
                    onChange={formik.handleChange}
                  >
                    <option value="" disabled>
                      Select call number
                    </option>
                    {user?.phoneNumber?.map((phone) => (
                      <option key={phone} value={phone}>
                        {phone}
                      </option>
                    ))}
                  </select>
                </div> */}

                <div className="mb-4">
                  <h5>
                    Assistants <span className="text-danger">*</span>
                  </h5>
                  <div
                    className="border rounded p-3"
                    style={{ maxHeight: "220px", overflowY: "auto" }}
                  >
                    {assistantOptions.length === 0 ? (
                      <p className="text-muted mb-0">
                        No assistants installed.
                      </p>
                    ) : (
                      assistantOptions.map((assistant) => {
                        let iconSrc = getIcon(assistant?.name);

                        if (!iconSrc) {
                          iconSrc = assistant?.name;
                        }

                        return (
                          <div
                            key={assistant.value}
                            className="d-flex align-items-center mb-2"
                          >
                            <input
                              id={`assistant-${assistant.value}`}
                              type="checkbox"
                              className="form-check-input mb-0 me-2"
                              style={{ cursor: "pointer" }}
                              checked={(
                                formik.values.assistants || []
                              ).includes(assistant.value)}
                              onChange={(e) => {
                                const current = formik.values.assistants || [];
                                if (e.target.checked) {
                                  formik.setFieldValue("assistants", [
                                    ...current,
                                    assistant.value,
                                  ]);
                                } else {
                                  formik.setFieldValue(
                                    "assistants",
                                    current.filter(
                                      (id) => id !== assistant.value,
                                    ),
                                  );
                                }
                              }}
                            />
                            <label
                              htmlFor={`assistant-${assistant.value}`}
                              className="mb-0"
                              style={{ cursor: "pointer" }}
                            >
                              <img
                                src={iconSrc}
                                alt={assistant.label}
                                className="me-2"
                                style={{
                                  width: "20px",
                                  height: "20px",
                                  objectFit: "contain",
                                }}
                              />

                              {assistant.label}
                            </label>
                          </div>
                        );
                      })
                    )}
                  </div>
                  {formik.touched.assistants && formik.errors.assistants && (
                    <div className="invalid-feedback d-block">
                      {formik.errors.assistants}
                    </div>
                  )}
                </div>

                <div className="d-flex justify-content-between">
                  <button
                    type="button"
                    className="btn btn-outline-danger px-3"
                    onClick={() => navigate(-1)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={formik.isSubmitting}
                  >
                    <i
                      className={`wb ${Icons("save")} fs-8 me-2 align-middle`}
                    />
                    {formik.isSubmitting ? "Creating..." : "Create Agent"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateAgent;
